/* eslint-disable eqeqeq */
import { handleTabToNext, looseEq } from '../../formKit';
import { airStreamOptions } from '../../airStreamOptions';
import { useAirStreamOfferedIds } from '../../useAirStreamOfferedIds';
import { ACCESSORY_FORM_CONFIGS } from '../accessoryFormConfigs';
import { useAccessoryForm, type AccessoryFormApi } from '../useAccessoryForm';
import {
  ABareInput,
  AccessorySelectHeading,
  AccSubtab,
  accList,
  AutoSizeRow,
} from '../accessoryFieldKit';
import AccessoryMountingPopup, { type SpecialParametersProps } from '../AccessoryMountingPopup';
import {
  ComplexMountingPlaneRow,
  ComplexPlaneRows,
  resolveComplexPlane,
} from '../complexMounting';
import { AirDirectionRow } from './LouverForm';
import type { AccessoryFormProps } from '../AccessoryFormHost';

/**
 * Phase 6e2b-2 — HOOD form (accessories/hood/{view/form.js 154,
 * tmpl/form.tmpl 505}, read in full) — built TOGETHER with damper (#335).
 * §51.2 row:
 *  - no catalogue field; CUSTOM autoSize (3:1; style 4 → height_2 = 2/3·h
 *    incl. style-change recalc; depth_mode 1 → depth = height) —
 *    defaultFaceVelocity 500 (parity 331);
 *  - 26 validatedFields incl. style/angles 1-4/depth_mode/height_2;
 *  - SelectionMode handler → Select heading;
 *  - hood draw needs ≥1 angle (chassis draw gate — parity 334);
 *  - hood w/h/tag PATCH success → select:hood:<id> → chassis selectHood
 *    resyncs every mounted damper (parity 304/335);
 *  - hood sole-mtl auto-set (chassis effect — parity 330).
 */
const config = ACCESSORY_FORM_CONFIGS.hood;

/** hood/view/form.js autoSize — VERBATIM port (3:1 + height_2 + depth_mode). */
const hoodAutoSize = (
  auto: boolean,
  model: { get<T = unknown>(k: string): T },
  data: Record<string, unknown>,
  faceVelocityInit?: number | string
): boolean | undefined => {
  if (!data.airflow_actual) return undefined;
  const existingWidth = model.get('width');
  const existingHeight = model.get('height');
  if (!auto && (existingWidth || existingHeight) && !data.style_id) return undefined;
  // Use data.style_id when style just changed (model still has old value).
  const styleId = data.style_id ?? model.get('style_id');
  const styleChanged = 'style_id' in data;
  const airflow = Number(data.airflow_actual);
  const faceVelocity = Number(faceVelocityInit) || 500;
  const calculatedHeight = Math.floor(Math.sqrt((144 * airflow) / (faceVelocity * 3)));
  const calculatedWidth = 3 * calculatedHeight;
  let changed = false;
  if (auto || !(existingWidth || existingHeight)) {
    if (calculatedWidth != existingWidth || calculatedHeight != existingHeight) {
      data.width = calculatedWidth;
      data.height = calculatedHeight;
      changed = true;
    }
  }
  if (styleId == 4) {
    const h = (data.height ?? existingHeight) as number | null;
    if (h && (auto || styleChanged || model.get('height_2') === null)) {
      data.height_2 = Math.round((Number(h) * 2) / 3);
      changed = true;
    }
  }
  if (model.get('depth_mode_id') == 1) {
    const h = (data.height ?? existingHeight) as number | null;
    if (h) {
      data.depth = h;
      changed = true;
    }
  }
  if (changed) return true;
  return undefined;
};

/** mounting_popup_special_parameters.tmpl (hood, 13 lines — air direction). */
const HoodSpecialParameters = ({ api, stageModelField, staged }: SpecialParametersProps) => {
  const options = accList(api.cmsAccessory, 'air_direction');
  const current = staged.air_direction_id ?? api.model?.get('air_direction_id');
  return (
    <ul>
      <li className="special-parameter-air_direction">
        <span className="label">Air Direction</span>
        <select
          name="air_direction_id"
          className="model_field_popup"
          required
          value={current == null ? 'None' : String(current)}
          onChange={(e) => stageModelField('air_direction_id', parseInt(e.target.value, 10))}
        >
          <option value="None">- -</option>
          {options.map((o) => (
            <option key={String(o.id)} value={String(o.id)}>
              {String(o.name ?? '')}
            </option>
          ))}
        </select>
      </li>
    </ul>
  );
};

const HOOD_STYLE_ICONS = [
  'hood_2_sided_curved',
  'hood_2_sided_straight',
  'hood_3_sided',
  'hood_4_sided',
  'hood_5_sided',
];

const SelectMirror = ({
  api,
  name,
  options,
  labelKey = 'name',
}: {
  api: AccessoryFormApi;
  name: string;
  options: Array<Record<string, unknown>>;
  labelKey?: string;
}) => {
  const current = options.find((o) => looseEq(o.id, api.model?.get(name)));
  return (
    <span className="selection">
      {current ? <span>{String(current[labelKey] ?? '')}</span> : null}
    </span>
  );
};

const HoodForm = ({ row, section, tunnel, expanded }: AccessoryFormProps) => {
  const api = useAccessoryForm({
    config,
    row,
    section,
    tunnel,
    expanded,
    labeledSubtabs: ['mounting', 'performance'],
    autoSize: hoodAutoSize,
    autoSizeGetState: (obj) => ({ width: obj?.width, height: obj?.height }),
    getFaceVelocityArea: (data) => {
      const m = data.model as Record<string, unknown> | undefined;
      const w = Number(m?.width);
      const h = Number(m?.height);
      return w && h ? w * h : null;
    },
    defaultFaceVelocity: 500,
  });
  // ★ Above the preloader early-return below: a hook that runs only
  // after the form has loaded changes the hook count between renders
  // (React #310, "Rendered more hooks than during the previous render").
  const offeredAirStreams = useAirStreamOfferedIds();
  const { model } = api;
  if (api.loading || !model) {
    return <div className="preloader dotted-preloader active" />;
  }
  const cid = row.id;
  const styleName = String(model.get('style') ?? '');
  const is45 = styleName === 'hood_5_sided' || styleName === 'hood_4_sided';
  const is34 = styleName === 'hood_3_sided' || styleName === 'hood_4_sided';
  const is4 = styleName === 'hood_4_sided';
  const styles = accList(api.cmsAccessory, 'style');
  const depthModes = accList(api.cmsAccessory, 'depth_mode');
  const mtls = accList(api.cmsAccessory, 'mtl');
  const screens = accList(api.cmsAccessory, 'screen');
  const airStreams = airStreamOptions(
    accList(api.cmsAccessory, 'air_stream'),
    offeredAirStreams,
    model.get('air_stream_id')
  );
  // airflow_face narrowed by style (cms rows: {style: {id}, airflow_face: []}).
  const airflowFaceGroups =
    (api.cmsAccessory?.airflow_face as Array<Record<string, unknown>>) ?? [];
  const airflowFaces =
    (airflowFaceGroups.find((g) =>
      looseEq((g.style as Record<string, unknown> | undefined)?.id, model.get('style_id'))
    )?.airflow_face as Array<Record<string, unknown>>) ?? [];
  const cmsPlanes = (api.cmsAccessory?.mounting_plane as Array<Record<string, unknown>>) ?? [];
  const resolved = resolveComplexPlane(
    api,
    undefined,
    cmsPlanes as Array<Record<string, unknown> & { id: number }>
  );
  const depthLocked = model.get('depth_mode_id') == 1;
  const mtlSole = mtls.length === 1;

  return (
    <>
      <AccessorySelectHeading api={api} type="hood" />
      <form className="hood_form" ref={(el) => { api.formRef.current = el; }} onKeyDown={handleTabToNext}>
        <ul className="inputs-group sublist">
          <AccSubtab api={api} type="hood" cid={cid} name="properties" sublistClass="max-height-auto non-clickable">
            <li>
              <div>
                <ABareInput api={api} name="tag" formatter="none" extraClass="text-left" />
                <i className="text-clear icon-close-1" />
                <span className="label">Tag</span>
              </div>
            </li>
            <li>
              <div className="single-line">
                {/* §JSX-WS (complexMounting.tsx:204) — every `{' '}` below is a
                    newline the legacy tmpl leaves between two inline(-block)
                    controls, i.e. a real ~4px space. JSX drops whitespace that
                    contains a newline, so without them each control after the
                    first sat 4px left of legacy, compounding along the row
                    (measured: the 4th angle box 16px out). */}
                <SelectMirror api={api} name="style_id" options={styles} />{' '}
                <select
                  className="medium-input  model_field"
                  name="style_id"
                  required
                  value={model.get('style_id') == null ? 'None' : String(model.get('style_id'))}
                  onChange={(e) => api.commitModelField('style_id', e.target.value)}
                >
                  {model.get('style_id') == null && (
                    <option value="None" className="hide">
                      - -
                    </option>
                  )}
                  {styles.map((o) => (
                    <option key={String(o.id)} value={String(o.id)}>
                      {String(o.name ?? '')}
                    </option>
                  ))}
                </select>{' '}
                <div className="hood-style-dialog-position">
                  <div className="hood-style-dialog">
                    {HOOD_STYLE_ICONS.filter((s) => s === styleName).map((s) => (
                      <span key={s} className={`icon-hood icon-${s}`} />
                    ))}
                  </div>
                </div>
                <span className="label">Style</span>
              </div>
            </li>
            <li>
              <div className="single-line">
                <span className="unit long-unit">in</span>{' '}
                <ABareInput api={api} name="width" extraClass="text-left medium-input" />
                {/* hood form.tmpl:61 — `class="label "`, no asterisk. */}
                <span className="label">Width</span>
              </div>
            </li>
            <li>
              <div className="single-line">
                <span className="unit long-unit">in</span>{' '}
                <ABareInput api={api} name="height" extraClass="text-left extra-small" />{' '}
                {is45 && (
                  /* form.tmpl:69 carries an inline `margin-left: 8px`, which
                     beats `.extra-small { margin-left: 13px }`
                     (ahu/styles.css:4410-4413). Without it the second box sat
                     13px out instead of legacy's 4px space + 8px. */
                  <ABareInput
                    api={api}
                    name="height_2"
                    extraClass="text-left extra-small"
                    style={{ marginLeft: 8 }}
                  />
                )}
                {/* hood form.tmpl:70 — `class="label "`, no asterisk. */}
                <span className="label">Height{is45 ? ' x Height-2 ' : ''}</span>
              </div>
            </li>
            <li>
              <div>
                <span className="unit long-unit">in</span>{' '}
                <ABareInput
                  api={api}
                  name="depth"
                  extraClass={`text-left medium-input${depthLocked ? ' non-clickable' : ''}`}
                  readOnly={!!depthLocked}
                />{' '}
                <SelectMirror api={api} name="depth_mode_id" options={depthModes} />{' '}
                <select
                  name="depth_mode_id"
                  className="model_field medium-input"
                  required
                  value={
                    model.get('depth_mode_id') == null ? 'None' : String(model.get('depth_mode_id'))
                  }
                  onChange={(e) => api.commitModelField('depth_mode_id', e.target.value)}
                >
                  {model.get('depth_mode_id') == null && (
                    <option value="None" className="hide">
                      - -
                    </option>
                  )}
                  {depthModes.map((o) => (
                    <option key={String(o.id)} value={String(o.id)}>
                      {String(o.name ?? '')}
                    </option>
                  ))}
                </select>
                {/* hood form.tmpl:87 — `class="label "`, no asterisk. */}
                <span className="label">Depth</span>
              </div>
            </li>
            <li>
              <div className="single-line">
                <span className="selection">
                  {airflowFaces.find((o) => looseEq(o.id, model.get('airflow_face_id'))) ? (
                    <span>
                      {String(
                        airflowFaces.find((o) => looseEq(o.id, model.get('airflow_face_id')))
                          ?.name ?? ''
                      )}
                    </span>
                  ) : null}
                </span>{' '}
                <select
                  name="airflow_face_id"
                  className="model_field long-input"
                  required
                  style={{ marginRight: 33 }}
                  value={
                    model.get('airflow_face_id') == null
                      ? 'None'
                      : String(model.get('airflow_face_id'))
                  }
                  onChange={(e) => api.commitModelField('airflow_face_id', e.target.value)}
                >
                  {model.get('airflow_face_id') == null && (
                    <option value="None" className="hide">
                      - -
                    </option>
                  )}
                  {airflowFaces.map((o) => (
                    <option key={String(o.id)} value={String(o.id)}>
                      {String(o.name ?? '')}
                    </option>
                  ))}
                </select>{' '}
                <span className="unit short-unit">ft2</span>{' '}
                <input
                  name="face_area"
                  className="model_field text-left xsmall-input non-clickable"
                  tabIndex={-1}
                  readOnly
                  type="text"
                  value={String(model.get('face_area') ?? '')}
                  onChange={() => undefined}
                />
                <span className="label">Airflow</span>
              </div>
            </li>
            <li>
              <div className="single-line">
                <span className="unit long-unit">deg</span>{' '}
                <ABareInput api={api} name="angle_1" extraClass="text-left extra-small" min={20} max={90} />{' '}
                {is34 && (
                  <ABareInput api={api} name="angle_2" extraClass="text-left extra-small" min={20} max={135} />
                )}{' '}
                {is34 && (
                  <ABareInput api={api} name="angle_3" extraClass="text-left extra-small" min={20} max={135} />
                )}{' '}
                {is4 && (
                  <ABareInput api={api} name="angle_4" extraClass="text-left extra-small" min={20} max={135} />
                )}
                {/* hood form.tmpl:135-146 — every angle span is `class=""`, and
                    the tmpl puts each on its own line, so the slashes read
                    "1 / 2 / 3 / 4". Without the `{' '}`s it was "1/ 2 / 3/ 4". */}
                <span className="label">
                  Angle <span>1</span>
                  {is34 && <> <span>/ 2 </span></>}
                  {is34 && <> <span>/ 3</span></>}
                  {is4 && <> <span>/ 4</span></>}
                </span>
              </div>
            </li>
            <li>
              <div className="single-line">
                <span className="selection">
                  {mtlSole ? (
                    <span>{String(mtls[0].label ?? mtls[0].model ?? '')}</span>
                  ) : mtls.find((o) => looseEq(o.id, model.get('mtl_id'))) ? (
                    <span>
                      {String(
                        mtls.find((o) => looseEq(o.id, model.get('mtl_id')))?.label ??
                          mtls.find((o) => looseEq(o.id, model.get('mtl_id')))?.model ??
                          ''
                      )}
                    </span>
                  ) : null}
                </span>{' '}
                <select
                  name="mtl_id"
                  className="model_field"
                  value={model.get('mtl_id') == null ? '' : String(model.get('mtl_id'))}
                  onChange={(e) => api.commitModelField('mtl_id', e.target.value)}
                >
                  <option disabled value="" className="hidden">
                    {' '}
                    ---{' '}
                  </option>
                  {mtls.map((o) => (
                    <option key={String(o.id)} value={String(o.id)}>
                      {String(o.label ?? o.model ?? '')}
                    </option>
                  ))}
                </select>
                <span className="label">Material</span>
              </div>
            </li>
            <li>
              <div className="single-line">
                <span className="selection">
                  {screens.find((o) => looseEq(o.id, model.get('screen_id'))) ? (
                    <span>
                      {String(screens.find((o) => looseEq(o.id, model.get('screen_id')))?.model ?? '')}
                    </span>
                  ) : null}
                </span>{' '}
                <select
                  name="screen_id"
                  className="model_field"
                  required
                  value={model.get('screen_id') == null ? '' : String(model.get('screen_id'))}
                  onChange={(e) => api.commitModelField('screen_id', e.target.value)}
                >
                  <option value=""> --- </option>
                  {screens.map((o) => (
                    <option key={String(o.id)} value={String(o.id)}>
                      {String(o.model ?? '')}
                    </option>
                  ))}
                </select>
                <span className="label">Screen</span>
              </div>
            </li>
          </AccSubtab>

          <AccSubtab
            api={api}
            type="hood"
            cid={cid}
            name="mounting"
            label="Mounting"
            liClass="mounting-section-wrapper"
            sublistClass="non-clickable non-clickable"
          >
            <ComplexMountingPlaneRow
              api={api}
              cabinetPlanes={cmsPlanes as Array<Record<string, unknown> & { id: number }>}
              selectionText={resolved.planeName}
              selectedValue={
                api.position && Number(api.position.get('mounting_plane_id'))
                  ? String(api.position.get('mounting_plane_id'))
                  : 'None'
              }
              selectId="mounting_plane_id"
            />
            <ComplexPlaneRows
              api={api}
              variant="hood"
              planeValue={resolved.planeValue}
              iwPlane={null}
            />
            <AirDirectionRow api={api} />
          </AccSubtab>

          <AccSubtab
            api={api}
            type="hood"
            cid={cid}
            name="performance"
            label="Performance"
            sublistClass="performance-sublist non-clickable"
          >
            <li>
              <div className="">
                <span className="unit">CFM</span>{' '}
                <span className="selection">
                  {airStreams.find((o) => looseEq(o.id, model.get('air_stream_id'))) ? (
                    <span>
                      {String(
                        airStreams.find((o) => looseEq(o.id, model.get('air_stream_id')))?.name ?? ''
                      )}
                    </span>
                  ) : null}
                </span>{' '}
                <select
                  className="coli-medium model_field"
                  name="air_stream_id"
                  required
                  value={
                    model.get('air_stream_id') == null ? 'None' : String(model.get('air_stream_id'))
                  }
                  onChange={(e) => api.commitModelField('air_stream_id', e.target.value)}
                >
                  <option value="None">- -</option>
                  {airStreams.map((o) => (
                    <option key={String(o.id)} value={String(o.id)}>
                      {String(o.name ?? '')}&nbsp;
                    </option>
                  ))}
                </select>{' '}
                <ABareInput
                  api={api}
                  name="airflow_actual"
                  formatter="integer"
                  extraClass="coli-medium"
                  min={Number(model.get('min_airflow')) || 0}
                  max={Number(model.get('max_airflow')) || 400000}
                />
                <span className="label">Air Stream | Flow</span>
              </div>
            </li>
            <AutoSizeRow
              api={api}
              tooltip="Hood will be automatically sized based on 3:1 width x height ratio, airflow and desired velocity"
            />
            <li>
              <div>
                <span className="unit short-unit">fpm</span>{' '}
                <input
                  type="text"
                  data-formatter_type="decimal"
                  autoComplete="off"
                  name="face_velocity"
                  className="coli-medium"
                  readOnly
                  tabIndex={-1}
                  value={String(model.get('face_velocity') ?? '')}
                  onChange={() => undefined}
                />{' '}
                <span className="unit long-unit">in WC</span>{' '}
                <input
                  type="text"
                  data-formatter_type="decimal"
                  autoComplete="off"
                  name="apd"
                  className="coli-medium"
                  readOnly
                  tabIndex={-1}
                  value={String(model.get('apd') ?? '')}
                  onChange={() => undefined}
                />
                <span className="label">Velocity / APD</span>
              </div>
            </li>
          </AccSubtab>
        </ul>
      </form>
      <div id={`popup_accessory_mounting_${row.id}`} className="popup-wrapper location-popup">
        <div className="popup-content popup-content-animation" />
      </div>
      <div id="popup_accessory_style_dialog" className="popup-wrapper location-popup">
        <div className="popup-content popup-content-animation" />
      </div>
      <AccessoryMountingPopup
        api={api}
        typeName="hood"
        typeValue="hood"
        SpecialParameters={HoodSpecialParameters}
      />
    </>
  );
};

export default HoodForm;
